import { useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import {
  WorksApi,
  WORK_TYPES,
  WORK_BULK_STATUSES,
  type WorkBulkChanges,
} from "../api/works";
import CreatorProfilePicker, {
  type CreatorProfileValue,
} from "./CreatorProfilePicker";
import ReasonField from "./reason/ReasonField";
import logger from "../analytics/logger";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  ids: string[];
  formatOptions: string[];
  onSaved: () => void;
};

/** Bulk-edit a subset of fields across the currently selected works. Each field
 * has a "Change" toggle; only toggled fields are sent. Type/format/creator-
 * profile patch the Work; Status flips each work's latest certification
 * (APPROVED re-generates its signed certificate). A reason is required. */
export default function BulkEditWorksModal({
  isOpen,
  onClose,
  ids,
  formatOptions,
  onSaved,
}: Props) {
  const toast = useToast();

  const [enType, setEnType] = useState(false);
  const [enFormat, setEnFormat] = useState(false);
  const [enProfile, setEnProfile] = useState(false);
  const [enStatus, setEnStatus] = useState(false);

  const [wtype, setWtype] = useState<string>(WORK_TYPES[0]);
  const [wformat, setWformat] = useState("");
  const [picker, setPicker] = useState<CreatorProfileValue>({
    creatorId: "",
    profileId: "",
  });
  const [status, setStatus] = useState<string>(WORK_BULK_STATUSES[0]);
  const [reason, setReason] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reasonInvalid, setReasonInvalid] = useState(false);
  const [prevOpen, setPrevOpen] = useState(isOpen);

  if (isOpen !== prevOpen) {
    setPrevOpen(isOpen);
    if (!isOpen) {
      setEnType(false);
      setEnFormat(false);
      setEnProfile(false);
      setEnStatus(false);
      setWtype(WORK_TYPES[0]);
      setWformat("");
      setPicker({ creatorId: "", profileId: "" });
      setStatus(WORK_BULK_STATUSES[0]);
      setReason("");
      setError(null);
      setReasonInvalid(false);
      setBusy(false);
    }
  }

  const anyEnabled = enType || enFormat || enProfile || enStatus;

  const buildChanges = (): WorkBulkChanges => {
    const changes: WorkBulkChanges = {};
    if (enType) changes.type = wtype;
    if (enFormat) changes.format = wformat.trim();
    if (enProfile) changes.creator_profile_id = picker.profileId;
    if (enStatus) changes.status = status as WorkBulkChanges["status"];
    return changes;
  };

  const handleSave = async () => {
    setError(null);
    if (!reason.trim()) {
      setReasonInvalid(true);
      return;
    }
    if (!anyEnabled) return;
    if (enFormat && !wformat.trim()) {
      setError("Enter a format or uncheck it.");
      return;
    }
    if (enProfile && !picker.profileId) {
      setError("Select a creator and profile, or uncheck it.");
      return;
    }
    setBusy(true);
    try {
      const res = await WorksApi.bulkUpdate(ids, buildChanges(), {
        reason: reason.trim(),
      });
      const n = res.updated.length;
      const extras: string[] = [];
      if (res.refused.length > 0)
        extras.push(`${res.refused.length} could not be updated (no longer exist).`);
      if (res.no_certification && res.no_certification.length > 0)
        extras.push(`${res.no_certification.length} had no certification to update.`);
      toast({
        status: "success",
        title: `Updated ${n} work${n === 1 ? "" : "s"}`,
        description: extras.join(" ") || undefined,
      });
      onSaved();
      onClose();
    } catch (e: unknown) {
      logger.error("bulk_edit_works_failed", e);
      const code =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setError(code || "Failed to update works");
    } finally {
      setBusy(false);
    }
  };

  const rowProps = (enabled: boolean) => ({ opacity: enabled ? 1 : 0.5 });

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => (busy ? null : onClose())}
      size="lg"
      closeOnOverlayClick={!busy}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Edit {ids.length} work{ids.length === 1 ? "" : "s"}
        </ModalHeader>
        <ModalCloseButton isDisabled={busy} />
        <ModalBody>
          {error && (
            <Alert status="error" mb={4}>
              <AlertIcon />
              {error}
            </Alert>
          )}
          <Text fontSize="sm" color="gray.600" mb={3}>
            Check a field to change it on all selected works. Unchecked fields are
            left unchanged.
          </Text>
          <Stack spacing={4}>
            {/* Type */}
            <HStack align="center" spacing={3} {...rowProps(enType)}>
              <Checkbox
                isChecked={enType}
                onChange={(e) => setEnType(e.target.checked)}
                isDisabled={busy}
                flexShrink={0}
                w="160px"
              >
                Type
              </Checkbox>
              <Select
                size="sm"
                value={wtype}
                onChange={(e) => setWtype(e.target.value)}
                isDisabled={!enType || busy}
              >
                {WORK_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </HStack>

            {/* Format */}
            <HStack align="center" spacing={3} {...rowProps(enFormat)}>
              <Checkbox
                isChecked={enFormat}
                onChange={(e) => setEnFormat(e.target.checked)}
                isDisabled={busy}
                flexShrink={0}
                w="160px"
              >
                Format
              </Checkbox>
              <Input
                size="sm"
                value={wformat}
                placeholder="e.g. mp3, google_doc, jpeg"
                list="bulk-work-formats"
                onChange={(e) => setWformat(e.target.value)}
                isDisabled={!enFormat || busy}
              />
              <datalist id="bulk-work-formats">
                {formatOptions.map((f) => (
                  <option key={f} value={f} />
                ))}
              </datalist>
            </HStack>

            {/* Status (certification) */}
            <HStack align="center" spacing={3} {...rowProps(enStatus)}>
              <Checkbox
                isChecked={enStatus}
                onChange={(e) => setEnStatus(e.target.checked)}
                isDisabled={busy}
                flexShrink={0}
                w="160px"
              >
                Status
              </Checkbox>
              <Box flex="1">
                <Select
                  size="sm"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  isDisabled={!enStatus || busy}
                >
                  {WORK_BULK_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
                {enStatus && status === "APPROVED" && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Approving re-generates each work's certificate signature/PDF.
                  </Text>
                )}
              </Box>
            </HStack>

            {/* Creator profile */}
            <Box {...rowProps(enProfile)}>
              <Checkbox
                isChecked={enProfile}
                onChange={(e) => setEnProfile(e.target.checked)}
                isDisabled={busy}
                mb={enProfile ? 2 : 0}
              >
                Reassign creator profile
              </Checkbox>
              {enProfile && (
                <Stack spacing={3} pl={6}>
                  <CreatorProfilePicker
                    value={picker}
                    onChange={setPicker}
                    disabled={busy}
                  />
                </Stack>
              )}
            </Box>

            <Box pt={1}>
              <ReasonField
                value={reason}
                onChange={(v) => {
                  setReason(v);
                  if (v.trim()) setReasonInvalid(false);
                }}
                isInvalid={reasonInvalid}
              />
            </Box>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={busy}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={busy}
            isDisabled={!anyEnabled || !reason.trim()}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
